import { formatDataGridValue } from './format';
import { isCellInRange } from './selection';
import type {
  DataGridCellAddress,
  DataGridCellRange,
  DataGridColumn,
  DataGridColumnDisplayOptions,
  DataGridRowId,
} from './types';

/**
 * Tab-separated, which is what every spreadsheet reads off the clipboard.
 *
 * A value carrying a tab, a newline or a quote would otherwise break the shape
 * of what lands there — one cell becoming two, or one row becoming two — so
 * those are quoted the way a CSV field is, doubling any quote inside.
 */
const escapeClipboardValue = (value: string): string =>
  /["\t\n\r]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;

const cellText = <TRow>(
  row: TRow,
  column: DataGridColumn<TRow>,
  displayOptions: DataGridColumnDisplayOptions
): string =>
  escapeClipboardValue(
    formatDataGridValue(column.getValue(row), displayOptions, column.type) ?? ''
  );

const columnLabel = <TRow>(column: DataGridColumn<TRow>): string =>
  escapeClipboardValue(
    typeof column.header === 'string' ? column.header : column.id
  );

export type DataGridClipboardPayload = {
  text: string;
  /** What the notice reports, so the user can tell a partial copy from a whole one. */
  cellCount: number;
};

export type BuildDataGridClipboardTextInput<TRow> = {
  rows: TRow[];
  getRowId: (row: TRow) => DataGridRowId;
  /** Visible columns in display order, indexed the way a range's `columnIndex` is. */
  columns: DataGridColumn<TRow>[];
  ranges: DataGridCellRange[];
  selectedRowIds: Set<DataGridRowId>;
  focusedCell: DataGridCellAddress | null;
  displayOptionsFor: (columnId: string) => DataGridColumnDisplayOptions;
};

/**
 * What Ctrl/Cmd+C puts on the clipboard, or null when there is nothing to copy.
 *
 * Reads the selection the same way the grid draws it, most specific first: a
 * cell selection is what the user pointed at, a row selection is a set of whole
 * records, and a bare focused cell is the fallback. Null rather than an empty
 * string when none of those hold, so the caller can leave the keystroke to the
 * browser instead of clearing the clipboard.
 */
export function buildDataGridClipboardText<TRow>({
  rows,
  getRowId,
  columns,
  ranges,
  selectedRowIds,
  focusedCell,
  displayOptionsFor,
}: BuildDataGridClipboardTextInput<TRow>): DataGridClipboardPayload | null {
  if (ranges.length > 0) {
    const addresses = ranges.flatMap((range) => [range.start, range.end]);
    const rowIndices = addresses.map((address) => address.rowIndex);
    const columnIndices = addresses.map((address) => address.columnIndex);
    const minRow = Math.max(0, Math.min(...rowIndices));
    const maxRow = Math.min(rows.length - 1, Math.max(...rowIndices));
    const minColumn = Math.max(0, Math.min(...columnIndices));
    const maxColumn = Math.min(columns.length - 1, Math.max(...columnIndices));
    if (maxRow < minRow || maxColumn < minColumn) {
      return null;
    }

    // The rectangle the ranges span, with anything outside them left blank.
    // Several disjoint ranges have no shape a spreadsheet can paste, and
    // collapsing them into one block at least keeps every copied value in the
    // row and column it came from.
    let cellCount = 0;
    const lines: string[] = [];
    for (let rowIndex = minRow; rowIndex <= maxRow; rowIndex += 1) {
      const row = rows[rowIndex];
      if (row === undefined) {
        continue;
      }
      const rowId = getRowId(row);
      const cells: string[] = [];
      for (
        let columnIndex = minColumn;
        columnIndex <= maxColumn;
        columnIndex += 1
      ) {
        const column = columns[columnIndex];
        if (column === undefined) {
          continue;
        }
        const address: DataGridCellAddress = {
          rowId,
          rowIndex,
          columnId: column.id,
          columnIndex,
        };
        const inRange = ranges.some((range) => isCellInRange(address, range));
        if (!inRange) {
          cells.push('');
          continue;
        }
        cellCount += 1;
        cells.push(cellText(row, column, displayOptionsFor(column.id)));
      }
      lines.push(cells.join('\t'));
    }
    return cellCount === 0 ? null : { text: lines.join('\n'), cellCount };
  }

  if (selectedRowIds.size > 0) {
    const selectedRows = rows.filter((row) =>
      selectedRowIds.has(getRowId(row))
    );
    if (selectedRows.length === 0 || columns.length === 0) {
      return null;
    }
    // Headed, unlike a cell selection: a whole record pasted somewhere else
    // arrives as a table of values with nothing saying which column is which,
    // and the reader has no cell selection of their own to read them against.
    const lines = [
      columns.map((column) => columnLabel(column)).join('\t'),
      ...selectedRows.map((row) =>
        columns
          .map((column) => cellText(row, column, displayOptionsFor(column.id)))
          .join('\t')
      ),
    ];
    return {
      text: lines.join('\n'),
      cellCount: selectedRows.length * columns.length,
    };
  }

  if (focusedCell !== null) {
    const row = rows[focusedCell.rowIndex];
    const column = columns[focusedCell.columnIndex];
    if (row === undefined || column === undefined) {
      return null;
    }
    return {
      text: cellText(row, column, displayOptionsFor(column.id)),
      cellCount: 1,
    };
  }

  return null;
}

/**
 * Reads a pasted block back into rows of cells.
 *
 * The mirror of what {@link buildDataGridClipboardText} writes, and of what a
 * spreadsheet puts on the clipboard: tabs between cells, newlines between rows,
 * and a quoted field for any value that contains one of those — so the quoting
 * has to be understood here rather than split naively, or a value with a
 * newline in it would arrive as two rows.
 */
export function parseDataGridClipboardText(text: string): string[][] {
  // A trailing newline is how most sources end the block; it is a terminator,
  // not an empty final row.
  const source = text.replace(/\r\n/g, '\n').replace(/\n$/, '');
  if (source === '') {
    return [];
  }

  const rows: string[][] = [];
  let cells: string[] = [];
  let value = '';
  let quoted = false;

  for (let index = 0; index < source.length; index += 1) {
    const character = source[index];
    if (quoted) {
      if (character !== '"') {
        value += character;
        continue;
      }
      // A doubled quote inside a quoted field is one literal quote.
      if (source[index + 1] === '"') {
        value += '"';
        index += 1;
        continue;
      }
      quoted = false;
      continue;
    }
    if (character === '"' && value === '') {
      quoted = true;
      continue;
    }
    if (character === '\t') {
      cells.push(value);
      value = '';
      continue;
    }
    if (character === '\n') {
      cells.push(value);
      rows.push(cells);
      cells = [];
      value = '';
      continue;
    }
    value += character;
  }
  cells.push(value);
  rows.push(cells);
  return rows;
}
